import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, Observable, shareReplay } from 'rxjs';
import { CharacterService } from '../../../services/LupinoApi/character.service';
import { DeleteCharacterModalComponent } from '../../modal/delete-character/delete-character.component';
import { UserService } from 'app/services/LupinoApi/user.service';
import { CharacterFilters } from '../../../types/CharacterFilters';
import { CharacterFiltersComponent } from '../filter-character/character-filters.component';
import { CharacterCardComponent } from '../character-card/character-card.component';
import { AsyncPipe } from '@angular/common';
import { Race } from '../../../models/race';
import { RaceService } from '../../../services/LupinoApi/race.service';
import { CharacterSearchRequest, CharacterSearchState, CharacterSummary, createCharacterSearchState, createDefaultCharacterFilters } from '../../../shared/character-search';

@Component({
	selector: 'app-characters',
	templateUrl: './characters.component.html',
	styleUrl: './characters.component.css',
	imports: [CharacterFiltersComponent, CharacterCardComponent, AsyncPipe],
})
export class CharactersComponent {
	raceService = inject(RaceService);
	currentUser$ = this.userService.getUserData().pipe(shareReplay({ bufferSize: 1, refCount: true }));
	races$: Observable<Race[]> = this.raceService.getRaces().pipe(shareReplay({ bufferSize: 1, refCount: true }));
	private path = this.router.url.split('?')[0];
	isMyCharacterPage = this.path === '/mycharacters';
	isMyPNJSPage = this.path === '/mypnjs';
	isPNJSPage = this.path === '/pnjs';
	raceMap$ = this.races$.pipe(
		map((races) => new Map(races.map((race) => [race._id, race.name]))),
		shareReplay({ bufferSize: 1, refCount: true }),
	);
	filters = createDefaultCharacterFilters();
	searchText = '';
	readonly emptyRaceMap = new Map<string, string>();
	totalPages(view: CharacterSearchState) { return Math.max(1, Math.ceil(view.total / view.pageSize)); }
	deleteError = '';
	private requests = new BehaviorSubject<CharacterSearchRequest>(this.makeRequest(1));
	view$: Observable<CharacterSearchState>;

	constructor(
		private characterService: CharacterService,
		private modalService: NgbModal,
		private router: Router,
		private userService: UserService,
	) {
		const identity = this.currentUser$.pipe(
			distinctUntilChanged((a, b) => a._id === b._id && a.isAdmin === b.isAdmin && a.isSuperAdmin === b.isSuperAdmin),
		);
		const requests = combineLatest([this.requests, identity]).pipe(map(([request]) => request));
		this.view$ = createCharacterSearchState(requests, (request) => this.characterService.searchCharacters(request));
	}

	private makeRequest(page: number): CharacterSearchRequest {
		return {
			...structuredClone(this.filters), search: this.searchText.trim().slice(0, 100),
			mine: this.isMyCharacterPage || this.isMyPNJSPage,
			...(this.isPNJSPage || this.isMyPNJSPage ? { isPNJ: true } : {}),
			page, pageSize: 24,
		};
	}

	onFiltersChange(filters: CharacterFilters) {
		this.filters = structuredClone(filters);
		this.requests.next(this.makeRequest(1));
	}

	onSearchChange(search: string) {
		this.searchText = search || '';
		this.requests.next(this.makeRequest(1));
	}

	goToPage(page: number) {
		this.requests.next(this.makeRequest(Math.max(1, page)));
	}

	retry() {
		this.requests.next(this.requests.value);
	}

	createNewCharacter() {
		this.router.navigate([this.isMyPNJSPage ? '/create-pnj' : '/create-character']);
	}

	openDeleteModal(character: CharacterSummary) {
		const modalRef = this.modalService.open(DeleteCharacterModalComponent);
		modalRef.componentInstance.character = character;
		modalRef.componentInstance.confirmDelete.subscribe(() => this.deleteCharacter(character._id));
	}

	deleteCharacter(id: string): void {
		this.deleteError = '';
		this.characterService.deleteCharacter(id).subscribe({
			next: (response) => {
				if (!response || (response as unknown as { result?: string }).result === 'ERROR') {
					this.deleteError = 'Impossible de supprimer ce personnage.';
					return;
				}
				this.retry();
			},
			error: () => this.deleteError = 'Impossible de supprimer ce personnage.',
		});
	}
}
